/**
 * BillingBanner
 *
 * Surfaces Stripe dunning state across every portal page. Reads
 * `billing_state` from the portal billing endpoint (written by the
 * Stripe webhook handler and grace-period cascade in CUR-431 Parts 1-2)
 * and renders:
 *
 * - `past_due` → amber banner + "Update payment" CTA that opens a Stripe
 *   Customer Portal session.
 * - `canceled` → red banner + "Upgrade plan" CTA linking to the portal
 *   billing page (the `manage` session would fail for a canceled sub;
 *   the billing page routes them through checkout instead).
 * - anything else → renders null.
 *
 * Dismissal is per-session and keyed by state: dismissing the canceled
 * banner won't suppress a future past_due banner, and a fresh session
 * resurfaces the banner so the underlying state stays visible.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CreditCard, Loader2, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';
import {
  usePortalBilling,
  useManageSubscription,
} from '@/hooks/api/usePortalBilling';

function formatGraceDate(iso: string | null): string {
  if (!iso) return 'soon';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return 'soon';
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const DISMISS_STORAGE_PREFIX = 'billing-banner-dismissed:';

function readDismissed(state: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(DISMISS_STORAGE_PREFIX + state) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(state: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(DISMISS_STORAGE_PREFIX + state, '1');
  } catch {
    // sessionStorage may be unavailable (privacy mode); failure is non-fatal.
  }
}

export function BillingBanner() {
  const { data: billing, isLoading } = usePortalBilling();
  const manage = useManageSubscription();
  const [ctaError, setCtaError] = useState<string | null>(null);
  const [dismissedTick, setDismissedTick] = useState(0);

  if (isLoading || !billing) return null;

  const state = billing.billing_state;
  if (state !== 'past_due' && state !== 'canceled') return null;

  // dismissedTick forces a re-read after the user clicks dismiss.
  void dismissedTick;
  if (readDismissed(state)) return null;

  const handleDismiss = () => {
    writeDismissed(state);
    setDismissedTick((n) => n + 1);
  };

  const handleUpdatePayment = async () => {
    setCtaError(null);
    try {
      const { portal_url } = await manage.mutateAsync({
        return_url: `${window.location.origin}/portal/billing`,
      });
      window.location.href = portal_url;
    } catch (_err) {
      setCtaError('Could not open the Stripe portal. Try again in a moment.');
    }
  };

  if (state === 'past_due') {
    return (
      <Alert
        role="alert"
        aria-live="polite"
        className={cn(
          'mb-4',
          'border-amber bg-amber-soft',
        )}
      >
        <CreditCard className="h-4 w-4 text-amber" />
        <AlertTitle>Your last payment failed</AlertTitle>
        <AlertDescription className="mt-1 space-y-2">
          <p>
            Update your card before{' '}
            <strong>{formatGraceDate(billing.grace_period_ends_at)}</strong>{' '}
            to keep your organization active. We'll keep trying the card on
            file, but after the grace window your subscription will be
            canceled.
          </p>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={handleUpdatePayment}
              disabled={manage.isPending}
              className="bg-amber hover:opacity-90 text-primary-foreground"
            >
              {manage.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening Stripe…
                </>
              ) : (
                'Update payment method'
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              aria-label="Dismiss banner for this session"
              className="text-amber hover:bg-amber-soft"
            >
              <X className="mr-1 h-4 w-4" />
              Dismiss
            </Button>
            {ctaError && (
              <span className="text-sm text-destructive" role="status">
                {ctaError}
              </span>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // canceled
  return (
    <Alert
      role="alert"
      aria-live="polite"
      variant="destructive"
      className="mb-4"
    >
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Your subscription has ended</AlertTitle>
      <AlertDescription className="mt-1 space-y-2">
        <p>
          Upgrade to restore access and add new sources, projects, or API
          tokens. Your existing data stays available while you're on the
          free tier — only new writes are paused.
        </p>
        <div className="flex items-center gap-3">
          <Button asChild size="sm" variant="outline">
            <Link to="/portal/billing">Upgrade plan</Link>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            aria-label="Dismiss banner for this session"
            className="text-destructive hover:bg-destructive/10"
          >
            <X className="mr-1 h-4 w-4" />
            Dismiss
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
